import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { localISODate, todayISO } from '../utils/dates';
import HistoryButton from './HistoryButton';

import DateInput from './DateInput';
const errorMessage = (error, fallback) => {
  const data = error.response?.data;
  return typeof data === 'string' && data ? data : fallback;
};
const day = (d) => (d ? new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const num = (v) => Number(v || 0);
const round2 = (v) => Math.round(num(v) * 100) / 100;

const blankTransfer = () => ({ date: todayISO(), from: '', to: '', amount: '', reference: '', notes: '' });
const blankLine = () => ({ account: '', debit: '', credit: '', note: '' });

/**
 * Accounts > Transfers & Journals. Moving money between your own cash and bank accounts (cash deposited, cash
 * withdrawn, bank to bank) takes one line; journal entries (capital brought in, drawings, loans, depreciation,
 * corrections) are for the Finance Manager and the Org Admin. Neither is edited: a mistake is undone.
 */
function Vouchers({ active, currency = 'INR', canJournal = false, onChanged }) {
  const [options, setOptions] = useState(null);
  const [form, setForm] = useState(blankTransfer);
  const [saving, setSaving] = useState(false);
  const [range, setRange] = useState(() => {
    const now = new Date();
    return [localISODate(new Date(now.getFullYear(), now.getMonth(), 1)), todayISO()];
  });
  const [data, setData] = useState(null);
  const [journalOpen, setJournalOpen] = useState(false);
  const [viewing, setViewing] = useState(null);

  const money = useMemo(() => {
    const f = new Intl.NumberFormat('en-IN', { style: 'currency', currency, minimumFractionDigits: 2 });
    return (v) => f.format(num(v));
  }, [currency]);

  const loadOptions = useCallback(() => {
    axios.get('/api/accounts/vouchers/options')
      .then((r) => setOptions(r.data))
      .catch((error) => toast.error(errorMessage(error, 'Could not load the accounts')));
  }, []);

  const load = useCallback(() => {
    axios.get('/api/accounts/vouchers', { params: { from: range[0], to: range[1] } })
      .then((r) => setData(r.data))
      .catch((error) => toast.error(errorMessage(error, 'Could not load the entries')));
  }, [range]);

  useEffect(() => { if (active) loadOptions(); }, [active, loadOptions]);
  useEffect(() => { if (active) load(); }, [active, load]);

  if (!active) return null;

  const accounts = options?.accounts || [];
  const label = (a) => `${a.kind === 'CASH' ? 'Cash' : 'Bank'}: ${a.name}`;

  const transfer = async (e) => {
    e.preventDefault();
    if (!form.from || !form.to) { toast.warn('Choose where the money came from and where it went'); return; }
    if (form.from === form.to) { toast.warn('Choose two different accounts'); return; }
    if (!(num(form.amount) > 0)) { toast.warn('Enter the amount'); return; }
    setSaving(true);
    try {
      const { data: saved } = await axios.post('/api/accounts/vouchers/transfers', {
        date: form.date, fromLedgerId: Number(form.from), toLedgerId: Number(form.to), amount: num(form.amount),
        reference: form.reference, notes: form.notes
      });
      toast.success(`${saved.number}: ${money(saved.amount)} from ${saved.from} to ${saved.to}`);
      setForm((f) => ({ ...blankTransfer(), date: f.date }));
      load();
      if (onChanged) onChanged();
    } catch (error) {
      toast.error(errorMessage(error, 'Could not record it'));
    } finally {
      setSaving(false);
    }
  };

  const undo = async (v) => {
    const what = v.kind === 'TRANSFER' ? `${v.number} (${money(v.amount)} from ${v.from} to ${v.to})` : `${v.number} (${v.narration})`;
    const reason = window.prompt(`Undo ${what}? Reversing entries are posted on the same date. Why? (optional)`);
    if (reason === null) return;
    try {
      await axios.post(`/api/accounts/vouchers/${v.kind === 'TRANSFER' ? 'transfers' : 'journals'}/${v.id}/undo`, { reason });
      toast.success(`${v.number} undone`);
      setViewing(null);
      load();
      if (onChanged) onChanged();
    } catch (error) {
      toast.error(errorMessage(error, 'Could not undo it'));
    }
  };

  const canUndo = (v) => v.status !== 'UNDONE' && (v.kind === 'TRANSFER' || canJournal);

  return (
    <div>
      <Card className="mb-3">
        <Card.Header className="py-2">
          <strong>Move money between cash and bank</strong> <span className="small text-muted">cash deposited, cash withdrawn, bank to bank</span>
        </Card.Header>
        <Card.Body>
          {!options ? <Spinner animation="border" size="sm" /> : accounts.length < 2 ? (
            <p className="text-muted mb-0">Add your cash-in-hand and bank accounts first (Accounts &rsaquo; Cash-in-Hand and Bank Accounts).</p>
          ) : (
            <Form onSubmit={transfer}>
              <Row className="g-2">
                <Col md={2}>
                  <Form.Label className="small mb-1">Date</Form.Label>
                  <DateInput size="sm" value={form.date} max={todayISO()} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                </Col>
                <Col md={3}>
                  <Form.Label className="small mb-1">From</Form.Label>
                  <Form.Select size="sm" value={form.from} onChange={(e) => setForm({ ...form, from: e.target.value })}>
                    <option value="">Choose</option>
                    {accounts.map((a) => <option key={a.ledgerId} value={a.ledgerId}>{label(a)}</option>)}
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Label className="small mb-1">To</Form.Label>
                  <Form.Select size="sm" value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })}>
                    <option value="">Choose</option>
                    {accounts.filter((a) => String(a.ledgerId) !== form.from).map((a) => <option key={a.ledgerId} value={a.ledgerId}>{label(a)}</option>)}
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Form.Label className="small mb-1">Amount</Form.Label>
                  <Form.Control size="sm" type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                </Col>
                <Col md={2}>
                  <Form.Label className="small mb-1">Slip / UTR no. (optional)</Form.Label>
                  <Form.Control size="sm" value={form.reference} maxLength={100} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
                </Col>
                <Col md={10}>
                  <Form.Control size="sm" value={form.notes} maxLength={500} placeholder="Note (optional), e.g. Day's cash takings"
                    onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </Col>
                <Col md={2} className="d-grid">
                  <Button size="sm" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Record'}</Button>
                </Col>
              </Row>
            </Form>
          )}
        </Card.Body>
      </Card>

      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
        <div className="d-flex flex-wrap align-items-center gap-2">
          <DateInput size="sm" value={range[0]} max={range[1]} style={{ width: 'auto' }} onChange={(e) => setRange([e.target.value, range[1]])} />
          <span className="small text-muted">to</span>
          <DateInput size="sm" value={range[1]} min={range[0]} style={{ width: 'auto' }} onChange={(e) => setRange([range[0], e.target.value])} />
        </div>
        {canJournal && <Button size="sm" variant="outline-primary" onClick={() => setJournalOpen(true)}>+ Journal entry</Button>}
      </div>
      <Table striped bordered hover responsive size="sm" className="small">
        <thead>
          <tr><th>Date</th><th>No.</th><th>What</th><th className="text-end">Amount</th><th>By</th><th /></tr>
        </thead>
        <tbody>
          {data && data.vouchers.length === 0 && <tr><td colSpan={6} className="text-center text-muted py-3">Nothing in this period.</td></tr>}
          {data && data.vouchers.map((v) => (
            <tr key={v.id} className={v.status === 'UNDONE' ? 'text-muted' : ''} style={{ cursor: 'pointer' }} onClick={() => setViewing(v)}>
              <td className="text-nowrap">{day(v.date)}</td>
              <td className="font-monospace text-nowrap">{v.number}</td>
              <td>
                {v.kind === 'TRANSFER' ? <>{v.from} &rarr; {v.to}</> : <><Badge bg="light" text="dark" className="border me-1">Journal</Badge>{v.narration}</>}
                {v.kind === 'TRANSFER' && v.narration && <div className="text-muted">{v.narration}</div>}
              </td>
              <td className="text-end text-nowrap">{v.status === 'UNDONE' ? <s>{money(v.amount)}</s> : money(v.amount)}</td>
              <td>{v.createdBy}</td>
              <td className="text-end" onClick={(e) => e.stopPropagation()}>
                {v.status === 'UNDONE'
                  ? <Badge bg="secondary" title={v.undoReason || ''}>Undone</Badge>
                  : canUndo(v) && <Button size="sm" variant="link" className="p-0" onClick={() => undo(v)}>Undo</Button>}
                <HistoryButton documentType="JournalVoucher" documentId={v.id} label={v.number} className="ms-1" />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {journalOpen && (
        <JournalForm options={options} money={money} onHide={() => setJournalOpen(false)}
          onPosted={(v) => { setJournalOpen(false); toast.success(`${v.number} posted`); load(); loadOptions(); if (onChanged) onChanged(); }} />
      )}

      <Modal show={Boolean(viewing)} onHide={() => setViewing(null)}>
        <Modal.Header closeButton>
          <Modal.Title as="h5">{viewing?.number}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewing && (
            <>
              <div className="small mb-2">
                {day(viewing.date)} · by {viewing.createdBy}{viewing.reference ? ` · ref. ${viewing.reference}` : ''}
              </div>
              {viewing.narration && <p className="small">{viewing.narration}</p>}
              <Table size="sm" bordered className="small mb-0">
                <thead><tr><th>Account</th><th className="text-end">Debit</th><th className="text-end">Credit</th></tr></thead>
                <tbody>
                  {viewing.lines.map((l, i) => (
                    <tr key={i}>
                      <td>{l.ledger}{l.note && <div className="text-muted">{l.note}</div>}</td>
                      <td className="text-end">{num(l.debit) > 0 ? money(l.debit) : ''}</td>
                      <td className="text-end">{num(l.credit) > 0 ? money(l.credit) : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {viewing.status === 'UNDONE' && (
                <p className="small text-muted mt-2 mb-0">Undone by {viewing.undoneBy}{viewing.undoReason ? `: ${viewing.undoReason}` : ''}</p>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {viewing && canUndo(viewing) && <Button variant="outline-danger" onClick={() => undo(viewing)}>Undo</Button>}
          <Button variant="secondary" onClick={() => setViewing(null)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

/** A journal entry: lines of accounts with a debit or a credit each, which must balance. */
function JournalForm({ options, money, onHide, onPosted }) {
  const [date, setDate] = useState(todayISO());
  const [narration, setNarration] = useState('');
  const [reference, setReference] = useState('');
  const [lines, setLines] = useState([blankLine(), blankLine()]);
  const [saving, setSaving] = useState(false);

  const groups = useMemo(() => {
    const out = new Map();
    (options?.ledgers || []).forEach((l) => { if (!out.has(l.group)) out.set(l.group, []); out.get(l.group).push(l); });
    return [...out.entries()];
  }, [options]);
  const key = (l) => (l.ledgerId ? `ledger:${l.ledgerId}` : `code:${l.code}`);

  const totalDebit = round2(lines.reduce((s, l) => s + num(l.debit), 0));
  const totalCredit = round2(lines.reduce((s, l) => s + num(l.credit), 0));
  const balanced = totalDebit > 0 && totalDebit === totalCredit;

  const setLine = (i, patch) => setLines(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  const post = async () => {
    const used = lines.filter((l) => l.account || num(l.debit) || num(l.credit));
    if (!narration.trim()) { toast.warn('Say what the entry is for'); return; }
    if (used.length < 2) { toast.warn('A journal entry needs at least two lines'); return; }
    if (used.some((l) => !l.account)) { toast.warn('Choose the account on every line'); return; }
    if (!balanced) { toast.warn('Debits and credits must be equal'); return; }
    setSaving(true);
    try {
      const { data } = await axios.post('/api/accounts/vouchers/journals', {
        date, narration, reference,
        lines: used.map((l) => {
          const [kind, value] = l.account.split(':');
          return {
            ledgerId: kind === 'ledger' ? Number(value) : null,
            code: kind === 'code' ? value : null,
            debit: num(l.debit) || null,
            credit: num(l.credit) || null,
            note: l.note
          };
        })
      });
      onPosted(data);
    } catch (error) {
      toast.error(errorMessage(error, 'Could not post it'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title as="h5">Journal entry</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-2 mb-3">
          <Col md={2}>
            <Form.Label className="small mb-1">Date</Form.Label>
            <DateInput size="sm" value={date} max={todayISO()} onChange={(e) => setDate(e.target.value)} />
          </Col>
          <Col md={7}>
            <Form.Label className="small mb-1">What it is for</Form.Label>
            <Form.Control size="sm" value={narration} maxLength={500} placeholder="e.g. Owner brought in capital; Depreciation for 2025-26"
              onChange={(e) => setNarration(e.target.value)} />
          </Col>
          <Col md={3}>
            <Form.Label className="small mb-1">Reference (optional)</Form.Label>
            <Form.Control size="sm" value={reference} maxLength={100} onChange={(e) => setReference(e.target.value)} />
          </Col>
        </Row>
        <Table size="sm" bordered className="small mb-2">
          <thead>
            <tr><th>Account</th><th style={{ width: 140 }} className="text-end">Debit</th><th style={{ width: 140 }} className="text-end">Credit</th><th>Note</th><th /></tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i}>
                <td>
                  <Form.Select size="sm" value={l.account} onChange={(e) => setLine(i, { account: e.target.value })}>
                    <option value="">Choose</option>
                    {groups.map(([group, items]) => (
                      <optgroup key={group} label={group}>
                        {items.map((x) => <option key={key(x)} value={key(x)}>{x.name}{x.ledgerId ? '' : ' (new)'}</option>)}
                      </optgroup>
                    ))}
                  </Form.Select>
                </td>
                <td>
                  <Form.Control size="sm" type="number" min="0" step="0.01" className="text-end" value={l.debit}
                    onChange={(e) => setLine(i, { debit: e.target.value, credit: e.target.value ? '' : l.credit })} />
                </td>
                <td>
                  <Form.Control size="sm" type="number" min="0" step="0.01" className="text-end" value={l.credit}
                    onChange={(e) => setLine(i, { credit: e.target.value, debit: e.target.value ? '' : l.debit })} />
                </td>
                <td><Form.Control size="sm" value={l.note} maxLength={255} onChange={(e) => setLine(i, { note: e.target.value })} /></td>
                <td className="text-center">
                  {lines.length > 2 && (
                    <Button size="sm" variant="link" className="p-0 text-danger" title="Remove line" onClick={() => setLines(lines.filter((_, j) => j !== i))}>
                      <i className="bi bi-x-lg" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            <tr className="fw-semibold">
              <td>
                <Button size="sm" variant="link" className="p-0" onClick={() => setLines([...lines, blankLine()])}>+ Add line</Button>
              </td>
              <td className="text-end">{money(totalDebit)}</td>
              <td className="text-end">{money(totalCredit)}</td>
              <td colSpan={2} className={balanced ? 'text-success' : 'text-danger'}>
                {balanced ? 'Balanced' : totalDebit || totalCredit ? `Difference ${money(Math.abs(totalDebit - totalCredit))}` : ''}
              </td>
            </tr>
          </tbody>
        </Table>
        <p className="small text-muted mb-0">
          Debit what the business gets or spends (an asset, an expense, money into the bank); credit where it came from
          (capital, a loan, income, money out of the bank). Customers&apos; accounts are settled with receipts and credit
          notes, and stock with Inventory &rsaquo; Stock Adjustments, so they aren&apos;t offered here. Accounts marked
          &ldquo;new&rdquo; are made when first used; others are added under Accounts &rsaquo; Ledgers.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancel</Button>
        <Button variant="primary" disabled={saving || !balanced} onClick={post}>{saving ? 'Posting...' : 'Post'}</Button>
      </Modal.Footer>
    </Modal>
  );
}

export default Vouchers;
